import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as charts from './charts';
import { FilesControlService } from 'src/app/_services/files-control.service';
import { pickBy } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-resume',
  templateUrl: './resume.component.html',
  styleUrls: ['./resume.component.css'],
})
export class ResumeComponent implements OnInit, OnDestroy {
  minDate: any;
  maxDate: any;

  filters;
  isLoading;

  hasActivitiesOverTime;
  hasAccessOverTime;
  hasDomainsFileSent;

  @Input() id;

  chartInstances: ApexCharts[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    public filesControlService: FilesControlService,
    public translateService: TranslateService
  ) {}

  ngOnInit() {
    this.setFilters();
  }

  refreshData() {
    this.isLoading = true;
    this.filesControlService
      .getFileSummaryById({ ...this.filters, id: this.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (
          res?.body?.activitiesOverTime &&
          res?.body?.activitiesOverTime?.length
        ) {
          this.renderActivitiesOverTime(res.body?.activitiesOverTime);
        }
        if (res?.body?.accessOverTime && res?.body?.accessOverTime?.length) {
          this.renderDocumentAccessByDepartment(res.body?.accessOverTime);
        }
        if (res?.body?.domainsFileSent && res?.body?.domainsFileSent?.length) {
          this.hasDomainsFileSent = true;
          this.renderDomainsWhereDocumentWasSent(res.body?.domainsFileSent);
        }
        this.isLoading = false;
      });
  }

  renderActivitiesOverTime(data) {
    const categories = data.map((item) =>
      new Date(item.month).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      })
    );
    const mailSentCount = data.map((item) => item.mailSentCount);
    const modifiedCount = data.map((item) => item.modifiedCount);
    const printedCount = data.map((item) => item.printedCount);
    const readCount = data.map((item) => item.readCount);
    const renamedCount = data.map((item) => item.renamedCount);
    const labelAppliedCount = data.map((item) => item.labelAppliedCount);
    const labelChangedCount = data.map((item) => item.labelChangedCount);
    const labelRemovedCount = data.map((item) => item.labelRemovedCount);
    const fileCreatedCount = data.map((item) => item.fileCreatedCount);
    const archivedCount = data.map((item) => item.archivedCount);
    const copiedToNetworkCount = data.map((item) => item.copiedToNetworkCount);
    const removableMediaCount = data.map((item) => item.removableMediaCount);

    charts.activitiesOverTimeOptions.series = [
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.mailSent'
        ),
        data: mailSentCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.modified'
        ),
        data: modifiedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.printed'
        ),
        data: printedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.read'
        ),
        data: readCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.renamed'
        ),
        data: renamedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.labelApplied'
        ),
        data: labelAppliedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.labelChanged'
        ),
        data: labelChangedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.labelRemoved'
        ),
        data: labelRemovedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.fileCreated'
        ),
        data: fileCreatedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.archived'
        ),
        data: archivedCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.copiedToNetwork'
        ),
        data: copiedToNetworkCount,
      },
      {
        name: this.translateService.instant(
          'utilities.filesControl.resume.removableMedia'
        ),
        data: removableMediaCount,
      },
    ];

    charts.activitiesOverTimeOptions.xaxis.categories = categories;

    const hasData = this.checkIfSeriesHasData(
      charts.activitiesOverTimeOptions.series
    );

    if (hasData) {
      this.hasActivitiesOverTime = true;

      var activitiesOverTime = new ApexCharts(
        document.querySelector('#activitiesOverTime'),
        charts.activitiesOverTimeOptions
      );
      activitiesOverTime.render();
      this.chartInstances.push(activitiesOverTime);
    }
  }

  renderDocumentAccessByDepartment(data) {
    const departments = data
      .filter((item) => item.department !== null)
      .map((item) => item.department);
    const counts = data
      .filter((item) => item.department !== null)
      .map((item) => item.count);

    charts.documentAccessByDepartmentOptions.series = [
      {
        name: 'Accesos',
        type: 'column',
        data: counts,
      },
    ];
    charts.documentAccessByDepartmentOptions.xaxis.categories = departments;

    const hasData = this.checkIfDeptosHasData(
      charts.documentAccessByDepartmentOptions.xaxis.categories
    );

    if (hasData) {
      this.hasAccessOverTime = true;

      var documentAccessByDepartment = new ApexCharts(
        document.querySelector('#documentAccessByDepartment'),
        charts.documentAccessByDepartmentOptions
      );
      documentAccessByDepartment.render();
      this.chartInstances.push(documentAccessByDepartment);
    }
  }

  renderDomainsWhereDocumentWasSent(data) {
    const seriesData = data.map((item) => item.count);
    const labels = data.map((item) => item.domain);

    charts.domainsWhereDocumentWasSentOptions.series = seriesData;
    charts.domainsWhereDocumentWasSentOptions.labels = labels;

    var domainsWhereDocumentWasSent = new ApexCharts(
      document.querySelector('#domainsWhereDocumentWasSent'),
      charts.domainsWhereDocumentWasSentOptions
    );
    domainsWhereDocumentWasSent.render();
    this.chartInstances.push(domainsWhereDocumentWasSent);
  }

  checkIfSeriesHasData(series): boolean {
    return series.some((serie) => serie.data[0] !== 0);
  }

  checkIfDeptosHasData(series): boolean {
    return series.some((serie) => serie.department);
  }

  setFilters() {
    this.clearCharts();
    const filters = {
      dateStart: this.minDate && this.formatDate(this.minDate),
      dateEnd: this.maxDate && this.formatDate(this.maxDate),
    };
    this.filters = {
      ...filters,
    };
    this.filters = pickBy(this.filters, (v) => v !== undefined);
    this.refreshData();
  }

  cleanFilters() {
    this.minDate = undefined;
    this.maxDate = undefined;
    this.filters = undefined;
    this.filters = {
      ...this.filters,
    };
    this.filters = pickBy(this.filters, (v) => v !== undefined);
    this.refreshData();
  }

  formatDate(selectedDate: Date): string {
    const day = selectedDate.getDate().toString().padStart(2, '0');
    const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const year = selectedDate.getFullYear().toString();

    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    // Emite un valor para cancelar la suscripción
    this.destroy$.next();
    // Completa el subject para liberar memoria
    this.destroy$.complete();
  }

  clearCharts() {
    document.getElementById('documentAccessByDepartment').innerHTML = '';
    document.getElementById('domainsWhereDocumentWasSent').innerHTML = '';
    document.getElementById('activitiesOverTime').innerHTML = '';
    this.chartInstances.forEach((chart) => {
      chart.destroy(); // Destruye la instancia del gráfico
    });
    this.chartInstances = []; // Limpia el array de instancias de gráficos
  }
}
